import React from 'react';
import {BaseSettingsForm, DatePickerField} from '@/components/forms';
import {Form, Row, Col} from 'react-bootstrap';
import {Button} from '@/components/ui';
import {useStoreActions} from 'easy-peasy';
import SearchSelect from '@/components/search_select/SearchSelect';
import FormHelpTooltip from '@/components/utils/FormHelpTooltip';
import {useAlert} from '@/components/alerts';
import * as yup from 'yup';
import i18n from 'i18n';
import {buildURL} from '@/utils/urlBuilder';

interface FormValues {
    accountId: number;
    folderPath: string[];
    active: boolean;
    channelId: number | null;
    syncStartDate: string | null;
}

interface EmailFetchTasksSettingsFormProps {
    objId?: number;
    onSave?: () => void;
}

const emailFetchTasksSchema = yup.object({
    accountId: yup.number()
        .integer(i18n.t('jt_account_id_valid.text'))
        .required(i18n.t('jt_account_required.text'))
        .min(1, i18n.t('jt_account_min.text')),
    folderPath: yup.array().of(
        yup.string()
            .max(255, i18n.t('jt_folder_max_255.text'))
            .required(i18n.t('jt_folder_required.text')),
    ).required(i18n.t('jt_folder_specify.text')),
    syncStartDate: yup.date().nullable(),
    channelId: yup.number().nullable()
        .integer(i18n.t('jt_channel_id_valid.text'))
        .min(1, i18n.t('jt_channel_id_min.text')),
    active: yup.boolean(),
});

const EmailFetchTasksSettingsForm: React.FC<EmailFetchTasksSettingsFormProps> = React.memo((
    {
        objId = 0,
        onSave = () => { },
    },
) => {
    const {showSuccess} = useAlert();
    const setSettingsModalOpen = useStoreActions((actions: any) => actions.settingsModalOpen.setSettingsModalOpen);
    const addOrEditAction = useStoreActions((actions: any) => actions.emailFetchTasks.addOrEdit);

    const initialValues: FormValues = {
        accountId: 0,
        folderPath: [''],
        active: true,
        channelId: null,
        syncStartDate: null,
    };

    const customSuccessHandling = (response: any, actionName: string) => {
        setSettingsModalOpen(false);
        addOrEditAction(response.data);

        if (objId) {
            showSuccess(i18n.t('jt_email_fetch_changed.text'));
        } else {
            showSuccess(i18n.t('jt_email_fetch_created.text'));
        }

        onSave();
    };

    return (
        <BaseSettingsForm
            entityId={objId || undefined}
            resourceUrl={window.URLS.emailFetchTasksResource}
            storeActionPath="emailFetchTasks.addOrEdit"
            validationSchema={emailFetchTasksSchema}
            initialValues={initialValues}
            successMessageKey="jt_save.text"
            customSuccessHandling={customSuccessHandling}
        >
            {(formik, loading) => (
                <>
                    <Form.Group className="mb-3" controlId="accountId" >
                        <Form.Label>{i18n.t('jt_select_the_account_where_emails_will_be_fetched_from.text')}</Form.Label>
                        <SearchSelect
                            optionsUrl={window.URLS.emailFetchAccountsResource}
                            formik={formik}
                            fieldName="accountId"
                            valueName="id"
                            labelName="email"
                            isPaginated={true}
                        />
                        <Form.Control.Feedback
                            type="invalid"
                            style={{
                                display: formik.touched.accountId && formik.errors.accountId ? 'block' : 'none',
                            }}
                        >
                            {formik.errors.accountId}
                        </Form.Control.Feedback>
                    </Form.Group>

                    <Form.Group className="mb-3" controlId="folderPath" >
                        <Form.Label> {i18n.t('jt_select_the_folder_where_emails_will_be_fetched_from.text')}</Form.Label>
                        <SearchSelect
                            optionsUrl={
                                buildURL('emailFetchAccountFoldersResource', {obj_id: formik.values.accountId})
                            }
                            formik={formik}
                            isDisabled={!formik.values.accountId}
                            selectKey={formik.values.accountId}
                            fieldName="folderPath"
                            getLabel={(value: string[]) => value.join(' -> ')}
                        />
                        <Form.Control.Feedback
                            type="invalid"
                            style={{
                                display: formik.touched.folderPath && formik.errors.folderPath ? 'block' : 'none',
                            }}
                        >
                            {formik.errors.folderPath}
                        </Form.Control.Feedback>
                    </Form.Group>

                    <Form.Group className="mb-3" controlId="channelId" >
                        <Form.Label> {i18n.t('jt_select_the_channel_where_synced_emails_will_be_stored.text')}</Form.Label>
                        <SearchSelect
                            optionsUrl={window.URLS.channelsResource}
                            requestParams={{
                                params: {
                                    'event_type.name': 'email',
                                },
                            }}
                            formik={formik}
                            fieldName="channelId"
                            valueName="id"
                            labelName="name"
                            isClearable
                            isPaginated={true}
                        />
                        <Form.Control.Feedback
                            type="invalid"
                            style={{
                                display: formik.touched.channelId && formik.errors.channelId ? 'block' : 'none',
                            }}
                        >
                            {formik.errors.channelId}
                        </Form.Control.Feedback>
                    </Form.Group >

                    <Form.Group className="mb-3" controlId="syncStartDate" >
                        <Form.Label>
                            {i18n.t('jt_select_the_starting_date_for_fetching.text')}
                            <FormHelpTooltip>
                                {i18n.t('jt_emails_before.text')}
                            </FormHelpTooltip>
                        </Form.Label>
                        <DatePickerField
                            name="syncStartDate"
                            value={formik.values.syncStartDate}
                            onChange={(value: string | null) => {
                                formik.setFieldValue('syncStartDate', value);
                            }}
                            isRequired={false}
                            error={formik.errors.syncStartDate}
                            touched={formik.touched.syncStartDate}
                            dateFormat="dd/MM/yyyy"
                            isClearable={true}
                        />
                    </Form.Group >

                    <Form.Group className="mb-3" >
                        <Form.Check
                            name="active"
                            label={i18n.t('es_active.text')}
                            type="switch"
                            onChange={formik.handleChange}
                            checked={formik.values.active}
                            isInvalid={!!formik.errors.active}
                            feedback={formik.errors.active}
                            id="emailCredentialsActive"
                        />
                    </Form.Group >

                    <Row className="mt-4">
                        <Col xs={12} className="d-flex justify-content-end">
                            <Button
                                type="submit"
                                variant="primary"
                                disabled={formik.isSubmitting}
                                loading={formik.isSubmitting}
                                data-cy="email-fetch-tasks-save-btn"
                            >
                                {objId ? i18n.t('jt_update.text') : i18n.t('jt_save.text')}
                            </Button>
                        </Col>
                    </Row>
                </>
            )}
        </BaseSettingsForm >
    );
});

export default EmailFetchTasksSettingsForm; 